import {
  Button,
  Text,
  TextInput,
  Select,
  Textarea,
  Modal,
  PasswordInput,
  Group,
  Space,
  ActionIcon,
} from "@mantine/core";
import React, { useState } from "react";
import { useFormRender } from "../hooks/useFormRender";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "./pageList";
import { showNotification } from "@mantine/notifications";
import {
  IconExclamationMark,
  IconCheck,
  IconRefresh,
  IconCopy,
} from "@tabler/icons";

const UserSetPasswordButton = ({ mainForm }) => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [loading, setLoading] = useState(false);

  // Function to generate random password
  const generateRandomPassword = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // Function to copy to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification({
        color: "green",
        icon: <IconCopy size={18} />,
        title: "Copied!",
        message: "Password has been copied to clipboard",
      });
    } catch (error) {
      console.log("Failed to copy to clipboard:", error);
      showNotification({
        color: "red",
        icon: <IconExclamationMark size={18} />,
        title: "Copy Failed",
        message: "Failed to copy password to clipboard",
      });
    }
  };

  // Create a simple form schema for password setting
  const passwordFormSchema = {
    title: "Set User Password",
    display: {
      mode: ENUM_FORM_DISPLAY.MODAL,
      size: "md",
    },
    showSaveBar: true,
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validate: {
      password: (value) => {
        if (!value) return "Password is required";
        if (value.length < 6) return "Password must be at least 6 characters";
        return null;
      },
      confirmPassword: (value, values) => {
        if (!value) return "Please confirm your password";
        if (value !== values.password) return "Passwords do not match";
        return null;
      },
    },
    layout: {
      containers: [],
      fields: [
        {
          name: "password",
          component: TextInput,
          parent: null,
          props: {
            required: true,
            label: "New Password",
            placeholder: "Enter new password",
            description: "Password must be at least 6 characters long",
            rightSection: (
              <ActionIcon
                onClick={() => {
                  const randomPassword = generateRandomPassword();
                  form.setFieldValue("password", randomPassword);
                  form.setFieldValue("confirmPassword", randomPassword);
                  copyToClipboard(randomPassword);
                }}
                variant="subtle"
                size="sm"
                title="Generate random password and copy to clipboard"
              >
                <IconRefresh size={16} />
              </ActionIcon>
            ),
          },
        },
        {
          name: "confirmPassword",
          component: TextInput,
          parent: null,
          props: {
            required: true,
            label: "Confirm Password",
            placeholder: "Confirm new password",
          },
        },
      ],
    },
  };

  const onSubmit = async ({ values, formMode }) => {
    try {
      setLoading(true);
      const id = mainForm?.values._id;
      if (!id) {
        showNotification({
          color: "red",
          icon: <IconExclamationMark size={18} />,
          title: "Error",
          message: "User ID not found",
        });
        return;
      }

      if (values.password !== values.confirmPassword) {
        showNotification({
          color: "red",
          icon: <IconExclamationMark size={18} />,
          title: "Error",
          message: "Passwords do not match",
        });
        return;
      }

      if (values.password.length < 6) {
        showNotification({
          color: "red",
          icon: <IconExclamationMark size={18} />,
          title: "Error",
          message: "Password must be at least 6 characters long",
        });
        return;
      }

      const result = await api.User.setPassword(id, values.password);

      if (result.success) {
        showNotification({
          color: "green",
          icon: <IconCheck size={18} />,
          title: "Success",
          message: "Password has been updated successfully",
        });
        formAction.close();
      } else {
        showNotification({
          color: "red",
          icon: <IconExclamationMark size={18} />,
          title: "Error",
          message: result.error || "Failed to update password",
        });
      }
    } catch (error) {
      console.log(error);
      showNotification({
        color: "red",
        icon: <IconExclamationMark size={18} />,
        title: "Error",
        message: "An error occurred while updating password",
      });
    } finally {
      setLoading(false);
    }
  };

  const [renderForm, formAction, formStatus, formSetting, form] = useFormRender(
    passwordFormSchema,
    null,
    onSubmit
  );

  const handleActionClick = () => {
    try {
      const id = mainForm?.values._id;
      if (!id) {
        showNotification({
          color: "red",
          icon: <IconExclamationMark size={18} />,
          title: "Error",
          message: "User ID not found",
        });
        return;
      }

      formAction.open({ mode: "add" });
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <Button fullWidth onClick={handleActionClick} loading={loading}>
        {t("Set Password")}
      </Button>
      {renderForm()}
    </>
  );
};

export default UserSetPasswordButton;
